import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { createStore } from "solid-js/store";
import { Portal } from "solid-js/web";
import { TransitionGroup } from "@fleet/ui";
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { applyRubberBand, createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";
import { closestSelector } from "@/lib/gestures/guards";
import { TOAST_SWIPE } from "@/lib/gestures/presets";

type ToastType = "success" | "error" | "info" | "warning";

interface ToastAction {
  /** Action-button text (already localized), e.g. "Deshacer". */
  label: string;
  onClick: () => void;
}

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  action?: ToastAction;
}

const TOAST_DURATION = 5000;
const RADIX_36 = 36;
const ID_LENGTH = 9;

const [toasts, setToasts] = createStore<Toast[]>([]);

function dismiss(id: string) {
  setToasts(toasts.filter((t) => t.id !== id));
}

export function showToast(
  message: string,
  type: ToastType = "info",
  options?: { action?: ToastAction; durationMs?: number },
) {
  const id = Math.random().toString(RADIX_36).slice(2, ID_LENGTH);
  setToasts([...toasts, { id, message, type, action: options?.action }]);

  setTimeout(() => {
    dismiss(id);
  }, options?.durationMs ?? TOAST_DURATION);
}

function ToastItem(props: { toast: Toast; onDismiss: (id: string) => void }) {
  const { t } = useI18n();
  const [tx, setTx] = createSignal(0);
  const [opacity, setOpacity] = createSignal(1);
  const [isSwiping, setIsSwiping] = createSignal(false);
  const [isAnimating, setIsAnimating] = createSignal(false);

  let surfaceRef: HTMLDivElement | undefined;

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: TOAST_SWIPE.thresholdPx,
    commitPx: TOAST_SWIPE.commitThresholdPx,
    fling: { ...TOAST_SWIPE.fling },
    haptics: true,
    hapticMs: TOAST_SWIPE.hapticMs,
    // Ignore drags that start on the close button so it stays tappable.
    canStart: ({ target }) => !closestSelector(target, "button, a"),
    onStart: () => {
      setIsSwiping(true);
      setIsAnimating(false);
    },
    onMove: (sample) => {
      const next = applyRubberBand(
        sample.dx,
        TOAST_SWIPE.rubberBandLimitPx,
        TOAST_SWIPE.rubberBandFactor,
      );
      setTx(next);
      const width = surfaceRef?.clientWidth ?? TOAST_SWIPE.rubberBandLimitPx;
      setOpacity(Math.max(0, 1 - Math.abs(next) / width));
    },
    onEnd: (sample) => {
      setIsSwiping(false);
      setIsAnimating(true);

      const committed = sample.isFling || Math.abs(tx()) > TOAST_SWIPE.commitThresholdPx;
      if (committed && sample.direction !== null) {
        const width = surfaceRef?.clientWidth ?? TOAST_SWIPE.rubberBandLimitPx;
        const sign = sample.direction === "left" ? -1 : 1;
        setTx(sign * (width + TOAST_SWIPE.rubberBandLimitPx));
        setOpacity(0);
        setTimeout(() => {
          props.onDismiss(props.toast.id);
        }, TOAST_SWIPE.dismissAnimationMs);
        return;
      }

      // Snap back to rest.
      setTx(0);
      setOpacity(1);
      setTimeout(() => {
        setIsAnimating(false);
      }, TOAST_SWIPE.snapBackMs);
    },
    onCancel: () => {
      setIsSwiping(false);
      setIsAnimating(true);
      setTx(0);
      setOpacity(1);
      setTimeout(() => {
        setIsAnimating(false);
      }, TOAST_SWIPE.snapBackMs);
    },
  });

  const handlers = pointerHandlers(recognizer);

  return (
    <div
      ref={surfaceRef}
      role={props.toast.type === "error" ? "alert" : "status"}
      aria-live={props.toast.type === "error" ? "assertive" : "polite"}
      // `touch-pan-y` lets the page scroll vertically while the recognizer
      // owns the horizontal axis for swipe-to-dismiss.
      class="flex touch-pan-y select-none items-center justify-between gap-3 rounded-lg border bg-popover p-4 shadow-lg"
      classList={{
        "border-success/30 text-success": props.toast.type === "success",
        "border-destructive/30 text-destructive": props.toast.type === "error",
        "border-primary/30 text-primary": props.toast.type === "info",
        "border-warning/30 text-warning": props.toast.type === "warning",
      }}
      style={{
        transform: `translate3d(${tx()}px, 0, 0)`,
        opacity: opacity(),
        transition: isSwiping()
          ? "none"
          : `transform ${TOAST_SWIPE.snapBackMs}ms cubic-bezier(0.32, 0.72, 0, 1), opacity ${TOAST_SWIPE.snapBackMs}ms cubic-bezier(0.32, 0.72, 0, 1)`,
        "will-change": isSwiping() || isAnimating() ? "transform, opacity" : undefined,
      }}
      onPointerDown={handlers.onPointerDown}
      onPointerMove={handlers.onPointerMove}
      onPointerUp={handlers.onPointerUp}
      onPointerCancel={handlers.onPointerCancel}
    >
      <div class="flex min-w-0 items-center gap-3">
        <Show when={props.toast.type === "success"}>
          <CheckCircle2 class="h-5 w-5 shrink-0" />
        </Show>
        <Show when={props.toast.type === "error"}>
          <AlertCircle class="h-5 w-5 shrink-0" />
        </Show>
        <Show when={props.toast.type === "warning"}>
          <AlertTriangle class="h-5 w-5 shrink-0" />
        </Show>
        <Show when={props.toast.type === "info"}>
          <Info class="h-5 w-5 shrink-0" />
        </Show>
        <span class="min-w-0 break-words text-sm font-medium">{props.toast.message}</span>
      </div>
      <Show when={props.toast.action}>
        {(action) => (
          <button
            type="button"
            onClick={() => {
              action().onClick();
              props.onDismiss(props.toast.id);
            }}
            data-testid="toast-action"
            class="shrink-0 rounded-md border border-current/25 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide transition-colors hover:bg-black/5"
          >
            {action().label}
          </button>
        )}
      </Show>
      <button
        type="button"
        // A11y: the icon is `aria-hidden` (lucide default), so without a label
        // this is an unnamed button for AT users.
        aria-label={t(msg`Dismiss notification`)}
        onClick={() => {
          props.onDismiss(props.toast.id);
        }}
        class="shrink-0 rounded-full p-1 transition-colors hover:bg-black/5"
      >
        <X class="h-4 w-4" />
      </button>
    </div>
  );
}

export function Toaster() {
  return (
    <Portal>
      {/*
        Mobile: anchored to the bottom edge spanning the full width with safe
        side margins so it never overflows horizontally or hides under the
        notch / home indicator. Desktop (sm+): a fixed-width stack tucked into
        the bottom-right corner (previous behaviour). All offsets respect
        `env(safe-area-inset-*)`.
      */}
      <div
        class={[
          "pointer-events-none fixed z-[300] flex flex-col gap-2",
          // Mobile: full width with safe side margins (no horizontal overflow).
          "left-[calc(0.75rem+var(--safe-area-inset-left))]",
          "right-[calc(0.75rem+var(--safe-area-inset-right))]",
          "bottom-[calc(0.75rem+var(--safe-area-inset-bottom))]",
          // Desktop: detach the left edge and cap the width → bottom-right stack.
          "sm:left-auto sm:w-full sm:max-w-[400px]",
        ].join(" ")}
      >
        <TransitionGroup name="toast">
          <For each={toasts}>
            {(toast) => (
              <div class="pointer-events-auto">
                <ToastItem toast={toast} onDismiss={dismiss} />
              </div>
            )}
          </For>
        </TransitionGroup>
      </div>
    </Portal>
  );
}
